/**
 * Leave and permit requests: employees ask from the app, the office decides here.
 *
 * Approving is a decision with consequences for the person asking, so it is behind its
 * own dialog with an optional note rather than a bare button on the row. Whether the
 * signed-in person may decide at all is the API's call (`canDecide`: an administrator,
 * or a manager when Settings lets managers), and the buttons say why they are off
 * instead of just being grey.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MobileList } from '@/components/MobileList';
import { Modal } from '@/components/Modal';
import { Pill, PillRow } from '@/components/Pill';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { useI18n, type TranslationKey } from '@/i18n';
import { api, ApiError, type LeaveConfig, type LeaveRequest, type LeaveStatus } from '@/lib/api';
import { dateShort } from '@/lib/format';
import type { Tone } from '@/lib/labels';
import { useTableState, type SortOption } from '@/lib/table';

type Tab = 'pending' | 'approved' | 'rejected' | 'all';
const TABS: Tab[] = ['pending', 'approved', 'rejected', 'all'];

const STATUS_TONE: Record<LeaveStatus, Tone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
};

const SORTS: SortOption<LeaveRequest>[] = [
  { key: 'start', labelKey: 'leave.period', value: (r) => r.startsOn },
  { key: 'person', labelKey: 'leave.person', value: (r) => r.person },
  { key: 'requested', labelKey: 'leave.requested', value: (r) => r.createdAt, defaultDirection: 'desc' },
];

export function LeavePage() {
  const { t, locale } = useI18n();
  const [tab, setTab] = useState<Tab>('pending');
  const [deciding, setDeciding] = useState<{ request: LeaveRequest; decision: 'approved' | 'rejected' } | null>(null);

  const config = useQuery({ queryKey: ['leave-config'], queryFn: api.leaveConfig });
  const isOffice = config.data?.isOffice ?? false;

  // Only the office reads other people's requests; for anyone else the list endpoint
  // answers 403, so it is not even asked.
  const list = useQuery({
    queryKey: ['leave', tab],
    queryFn: () => api.leave(tab),
    enabled: isOffice,
  });

  const table = useTableState<LeaveRequest>({
    rows: list.data?.requests ?? [],
    search: (r) => [r.person, typeLabel(config.data, r.kind, 'it'), typeLabel(config.data, r.kind, 'en'), r.note],
    sorts: SORTS,
    defaultSortKey: 'start',
    storageKey: 'leave',
  });

  if (config.isLoading) return <Spinner />;
  if (config.error) return <ErrorNotice error={config.error} />;

  const cfg = config.data!;

  return (
    <>
      <PageHeader title={t('leave.title')} subtitle={t('leave.subtitle')} />

      {!cfg.isOffice ? (
        <Card>
          <p className="text-sm text-muted">{t('leave.officeOnly')}</p>
        </Card>
      ) : (
        <>
          {!cfg.enabled ? (
            <Card className="mb-4">
              <p className="text-sm text-muted">{t('leave.disabled')}</p>
            </Card>
          ) : null}
          {!cfg.canDecide ? (
            <Card className="mb-4 bg-[var(--color-primary-soft)] border-[var(--color-primary)]">
              <p className="text-sm">{t('leave.cannotDecide')}</p>
            </Card>
          ) : null}

          <PillRow className="mb-4">
            {TABS.map((x) => (
              <Pill key={x} active={tab === x} onClick={() => setTab(x)}>
                {t(`leave.tabs.${x}` as TranslationKey)}
              </Pill>
            ))}
          </PillRow>

          <DataToolbar
            state={table}
            searchPlaceholder={t('leave.searchPlaceholder')}
            countOne="leave.countOne"
            countMany="leave.countMany"
          />

          {list.isLoading ? (
            <Spinner />
          ) : list.error ? (
            <ErrorNotice error={list.error} />
          ) : (list.data?.requests.length ?? 0) === 0 ? (
            <Card>
              <p className="py-10 text-center text-sm text-muted">{t('leave.empty')}</p>
            </Card>
          ) : (
            <Card padded={false}>
              <div className="lg:hidden p-2">
                <MobileList<LeaveRequest>
                  rows={table.rows}
                  keyOf={(r) => r.id}
                  title={(r) => r.person}
                  subtitle={(r) => `${typeLabel(cfg, r.kind, locale)} · ${period(r, t)}`}
                  badges={(r) => <StatusBadge status={r.status} />}
                  fields={[
                    { label: t('leave.note'), value: (r) => r.note },
                    { label: t('leave.decision'), value: (r) => <Decision request={r} /> },
                    { label: t('leave.requested'), value: (r) => dateShort(r.createdAt) },
                  ]}
                  action={(r) =>
                    r.status === 'pending' ? (
                      <Actions cfg={cfg} request={r} onDecide={(decision) => setDeciding({ request: r, decision })} />
                    ) : null
                  }
                />
              </div>

              <div className="table-scroll max-lg:hidden">
                <table className="table-wide">
                  <thead>
                    <tr>
                      <SortableTh state={table} sortKey="person">
                        {t('leave.person')}
                      </SortableTh>
                      <th className="th">{t('leave.type')}</th>
                      <SortableTh state={table} sortKey="start">
                        {t('leave.period')}
                      </SortableTh>
                      <th className="th">{t('leave.note')}</th>
                      <th className="th">{t('common.status')}</th>
                      <th className="th">{t('leave.decision')}</th>
                      <th className="th" />
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((r) => (
                      <tr key={r.id} className="hover:bg-surface-alt align-top">
                        <td className="td font-medium whitespace-nowrap">{r.person}</td>
                        <td className="td text-sm">{typeLabel(cfg, r.kind, locale)}</td>
                        <td className="td text-sm tabular">{period(r, t)}</td>
                        <td className="td text-sm text-muted max-w-xs whitespace-pre-line break-words">{r.note ?? '-'}</td>
                        <td className="td">
                          <StatusBadge status={r.status} />
                        </td>
                        <td className="td text-sm">
                          <Decision request={r} />
                        </td>
                        <td className="td text-end">
                          {r.status === 'pending' ? (
                            <div className="flex justify-end gap-2">
                              <Actions
                                cfg={cfg}
                                request={r}
                                onDecide={(decision) => setDeciding({ request: r, decision })}
                              />
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {table.rows.length === 0 ? <NoResults onClear={table.clear} /> : null}
            </Card>
          )}
        </>
      )}

      <DecideDialog
        target={deciding}
        label={deciding ? typeLabel(config.data, deciding.request.kind, locale) : ''}
        onClose={() => setDeciding(null)}
      />
    </>
  );
}

/**
 * The name of a request type in the given language. A
 * type that was removed in Settings after the request was made has no name any more, so
 * the code it was saved under is shown instead of nothing.
 */
function typeLabel(config: LeaveConfig | undefined, key: string, language: 'it' | 'en' | 'ar'): string {
  const found = config?.types.find((x) => x.key === key);
  // Type names are kept in Italian and English only; Arabic readers get the English one.
  return found ? found[language === 'it' ? 'it' : 'en'] : key;
}

function period(r: LeaveRequest, t: (key: TranslationKey, values?: Record<string, string | number>) => string): string {
  if (r.startTime && r.endTime) {
    return `${dateShort(r.startsOn)}, ${t('leave.hoursRange', { from: r.startTime, to: r.endTime })}`;
  }
  return r.endsOn === r.startsOn ? dateShort(r.startsOn) : `${dateShort(r.startsOn)} – ${dateShort(r.endsOn)}`;
}

function StatusBadge({ status }: { status: LeaveStatus }) {
  const { t } = useI18n();
  const key = `leave.status.${status}` as TranslationKey;
  const text = t(key);
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{text === key ? status : text}</Badge>;
}

/** Who decided, when, and what they said. Empty while the request is still waiting. */
function Decision({ request }: { request: LeaveRequest }) {
  const { t } = useI18n();
  if (!request.decidedByName) return <span className="text-subtle">{'-'}</span>;
  return (
    <span>
      {t('leave.decidedBy', { name: request.decidedByName })}
      {request.decidedAt ? <span className="text-muted">{`, ${dateShort(request.decidedAt)}`}</span> : null}
      {request.decisionNote ? <span className="block text-xs text-muted whitespace-pre-line">{request.decisionNote}</span> : null}
    </span>
  );
}

function Actions({
  cfg,
  request,
  onDecide,
}: {
  cfg: LeaveConfig;
  request: LeaveRequest;
  onDecide: (decision: 'approved' | 'rejected') => void;
}) {
  const { t } = useI18n();
  const off = !cfg.canDecide;
  // The reason sits in a title as well as in the notice above the list, so a disabled
  // button explains itself where the pointer is.
  const why = off ? t('leave.cannotDecide') : undefined;
  return (
    <>
      <button
        type="button"
        className="btn-primary"
        disabled={off}
        title={why}
        aria-label={`${t('leave.approve')}: ${request.person}`}
        onClick={() => onDecide('approved')}
      >
        {t('leave.approve')}
      </button>
      <button
        type="button"
        className="btn-danger"
        disabled={off}
        title={why}
        aria-label={`${t('leave.reject')}: ${request.person}`}
        onClick={() => onDecide('rejected')}
      >
        {t('leave.reject')}
      </button>
    </>
  );
}

function DecideDialog({
  target,
  label,
  onClose,
}: {
  target: { request: LeaveRequest; decision: 'approved' | 'rejected' } | null;
  label: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const decide = useMutation({
    mutationFn: () => api.decideLeave(target!.request.id, target!.decision, note.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      setNote('');
      onClose();
    },
  });

  const approving = target?.decision === 'approved';
  const close = () => {
    setNote('');
    decide.reset();
    onClose();
  };

  return (
    <Modal
      open={target !== null}
      onClose={close}
      size="sm"
      title={approving ? t('leave.approveTitle') : t('leave.rejectTitle')}
      description={target ? `${target.request.person} · ${label} · ${period(target.request, t)}` : undefined}
      footer={
        <>
          <button
            type="button"
            className={approving ? 'btn-primary' : 'btn-danger'}
            disabled={decide.isPending}
            onClick={() => decide.mutate()}
          >
            {decide.isPending ? t('common.saving') : approving ? t('leave.approve') : t('leave.reject')}
          </button>
          <button type="button" className="btn-secondary" onClick={close}>
            {t('common.cancel')}
          </button>
          {decide.error ? (
            <p role="alert" className="text-sm text-danger ms-auto">
              {decide.error instanceof ApiError ? decide.error.message : t('common.saveError')}
            </p>
          ) : null}
        </>
      }
    >
      <p className="text-sm text-muted mb-4">{t('leave.dialogHint')}</p>
      <Field label={t('leave.noteLabel')}>
        <textarea
          className="input h-24 py-2"
          maxLength={500}
          value={note}
          placeholder={t('leave.notePlaceholder')}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Modal>
  );
}
